/**
 * features/exams/ClassSubjectsTab.tsx
 * ------------------------------------------------------------------
 * Exams · Class subjects & teachers. Pick a class → a table of every
 * student, the subjects they take, and who teaches each. Export a
 * branded PDF for the exams office.
 * ------------------------------------------------------------------
 */
import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { sortClasses } from '@/lib/classSort';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildClassSubjectsPdf, type ClassSubjectsRow } from '@/lib/examPdf';
import { SoftCard, AccentButton, glassFieldClass } from '@/components/ui/glass';
import type { Teacher } from '@/types';

const teacherLabel = (t?: Teacher) =>
  t ? (t.displayName?.trim() || `${t.title ?? ''} ${t.lastName ?? ''}`.trim()) : 'unassigned';

export default function ClassSubjectsTab() {
  const { activeStudents, subjects, classes, teacherById } = useData();
  const { userData } = useAuth();
  const [classId, setClassId] = useState('');

  const sortedClasses = useMemo(
    () =>
      sortClasses(classes),
    [classes],
  );

  const cls = classes.find((c) => c.id === classId);

  const rows: ClassSubjectsRow[] = useMemo(() => {
    if (!classId) return [];
    return activeStudents
      .filter((s) => s.currentClass === classId)
      .sort((a, b) => a.fullName.localeCompare(b.fullName))
      .map((s, i) => ({
        no: i + 1,
        name: s.fullName,
        adm: s.admissionNumber ?? '',
        subjects: (s.subjects ?? [])
          .map((sid) => ({
            subject: subjects.find((x) => x.id === sid)?.name ?? sid,
            teacher: teacherLabel(teacherById(s.subjectTeachers?.[sid] ?? '')),
          }))
          .sort((a, b) => a.subject.localeCompare(b.subject)),
      }));
  }, [classId, activeStudents, subjects, teacherById]);

  async function exportPdf() {
    if (!cls || rows.length === 0) return;
    const logo = await loadLogoDataUrl();
    buildClassSubjectsPdf({
      className: cls.name,
      metaLine: `${cls.name} · ${rows.length} students`,
      rows,
      logoDataUrl: logo,
      generatedBy: userData?.displayName,
    }).save(`ClassSubjects_${cls.name}`.replace(/\s+/g, '_') + '.pdf');
  }

  return (
    <div className="space-y-4">
      <SoftCard pad="p-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-gray-500">Class</label>
            <select value={classId} onChange={(e) => setClassId(e.target.value)} className={glassFieldClass('mt-1 sm:w-72')}>
              <option value="">— select class —</option>
              {sortedClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {rows.length > 0 && (
            <AccentButton variant="soft" icon={Download} onClick={exportPdf}>Export PDF</AccentButton>
          )}
        </div>
      </SoftCard>

      {!classId ? (
        <SoftCard><p className="text-sm text-gray-500 text-center py-8">Select a class to list its students, subjects and teachers.</p></SoftCard>
      ) : rows.length === 0 ? (
        <SoftCard><p className="text-sm text-gray-500 text-center py-8">No students in {cls?.name}.</p></SoftCard>
      ) : (
        <SoftCard pad="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-left text-gray-500">
                  <th className="px-3 py-2.5 w-8">#</th>
                  <th className="px-3 py-2.5 min-w-[160px]">Student</th>
                  <th className="px-3 py-2.5 w-20">Adm.</th>
                  <th className="px-3 py-2.5">Subjects — Teacher</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.no} className="border-b border-gray-50 dark:border-gray-700/50 align-top">
                    <td className="px-3 py-2 text-gray-400">{r.no}</td>
                    <td className="px-3 py-2 font-medium text-gray-800 dark:text-white">{r.name}</td>
                    <td className="px-3 py-2 text-gray-500">{r.adm || '—'}</td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                      {r.subjects.length === 0 ? (
                        <span className="text-gray-400">— no subjects —</span>
                      ) : (
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          {r.subjects.map((s) => (
                            <span key={s.subject}>
                              <span className="font-medium">{s.subject}</span>
                              <span className="text-gray-400"> — {s.teacher}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SoftCard>
      )}
    </div>
  );
}
